/**
 * How a conversation is DATED and NAMED in a list. Pure functions, no runtime imports.
 *
 * Its own module because `lib/conversations.ts` reaches `api-client` → common-lib's `rpc` barrel → the
 * ESM-only `natural`, which this jest config cannot parse in CI (see the Tests section of the package
 * CLAUDE.md). They are re-exported from `lib/conversations.ts`, so no call site's import changes.
 */

/** Short TIME for something that happened today, short DATE otherwise — with the year only when it
 *  differs from this one. `''` for a missing or unparseable value, never a fabricated date. */
export function formatConversationDate(value: string | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (sameDay) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString(
    [],
    sameYear ? { month: 'short', day: 'numeric' } : { year: 'numeric', month: 'short', day: 'numeric' },
  );
}

/**
 * What to CALL a conversation in a list: its own name, else WHEN it started, else the localized
 * "Untitled" word.
 *
 * The middle step is the point. A great many conversations legitimately carry no name — the namer that
 * writes one lives in the VMR conversation-message service
 * (`ConsumersImpl.generateConversationNameIfNeeded`) and returns early both when the conversation is not
 * yet `active` and once it already has five USER/ASSISTANT turns, so a system-initiated thread (a morning
 * digest, a dashboard build) can reach the list having never been named at all. QA saw the result: three
 * rows all reading "Untitled", indistinguishable from one another. A start time tells those three apart
 * and is a fact we hold, where the bare word is neither.
 *
 * `untitled` is the last resort, for a row with no parseable timestamp — never a fabricated one.
 *
 * Mirrored on both phones (`conversationDisplayTitle`, iOS `ChatsModels.swift` / Android
 * `ChatsModels.kt`) so one conversation reads the same on web, iOS and Android.
 */
export function conversationDisplayTitle(
  name: string | undefined,
  createdDate: string | undefined,
  untitled: string,
): string {
  const named = name?.trim();
  if (named) return named;
  return formatConversationDate(createdDate) || untitled;
}

/** A brief (source=BRIEF): a morning or meeting brief the system created, which the user can chat in.
 *  The list carries them alongside the user's chats (as the phones' drawer does), but a brief is never
 *  the RESUMABLE thread — the phones open it with `source: "BRIEF"` precisely so it stays out of the
 *  persisted last-conversation pointer — so every "which chat do we resume" read skips it.
 *
 *  Lives here, not in `lib/conversations.ts` (which re-exports it), so a suite that mocks
 *  `lib/conversations` wholesale cannot strip it from a page that needs it (chats-page.test.ts). */
export function isBriefConversation(c: { source?: string }): boolean {
  return c.source === 'BRIEF';
}

// How long after creation an inactive, not-failed brief still counts as convening. The same 6h window as
// `lib/brief.ts` (`BRIEF_IN_MOTION_WINDOW_MS`) and the phones' `BriefLifecycle.isInMotion`; repeated here
// because that module imports the API client, which this one must not.
const BRIEF_ROW_IN_MOTION_WINDOW_MS = 6 * 60 * 60 * 1000;

/** Whether a BRIEF row is still convening: not yet wrapped up (`active` flips at wrap-up), not failed, and
 *  created inside the window. Mirrors iOS `SymphonyData.BriefLifecycle.isInMotion`, which both phones'
 *  drawers call for a brief row. */
export function briefRowInMotion(
  row: { active?: boolean; briefFailed?: boolean; createdDate?: string },
  now: number = Date.now(),
): boolean {
  if (row.active || row.briefFailed) return false;
  const created = row.createdDate ? new Date(row.createdDate).getTime() : NaN;
  if (Number.isNaN(created)) return false;
  return now - created < BRIEF_ROW_IN_MOTION_WINDOW_MS;
}

/**
 * What to CALL a brief (source=BRIEF) in a list: the meeting's topic — the conversation `name`, which the
 * facilitator writes at wrap-up and the user can rename — else the brief label, suffixed "· In progress"
 * while it is still convening.
 *
 * Mirrors the phones' drawer row (iOS `DrawerOverlay.briefRowTitle`, Android `Standup.briefRowTitle`); the
 * label words come from the caller, so web keeps its own `brief.title` / `brief.inProgress` keys.
 */
export function briefRowTitle(name: string | undefined, inMotion: boolean, briefLabel: string, inProgressLabel: string): string {
  const topic = name?.trim();
  if (topic) return topic;
  return inMotion ? `${briefLabel} · ${inProgressLabel}` : briefLabel;
}
